import { DifficultyLevel } from "./product.service";

export enum ProductType {
  TOUR = "tour",
  ACTIVITY = "activity",
}

export enum ProductClassification {
  STANDARD_TOUR = "STANDARD_TOUR",
  TRANSFER = "TRANSFER",
  SHUTTLE = "SHUTTLE",
  FLIGHT = "FLIGHT",
}

export enum ProductStatus {
  DRAFT = "draft",
  PENDING_REVIEW = "pending_review",
  PUBLISHED = "published",
  REJECTED = "rejected",
  UNPUBLISHED = "unpublished",
  ARCHIVED = "archived",
}

export enum ConfirmationType {
  MANUAL = "manual",
  INSTANT = "instant",
}

export enum SessionStatus {
  ACTIVE = "active",
  SOLD_OUT = "sold_out",
  CANCELLED = "cancelled",
  PENDING_CONFIRMATION = "pending_confirmation",
}

export enum BookingStatus {
  PENDING = "pending",
  AWAITING_PAYMENT = "awaiting_payment",
  CONFIRMED = "confirmed",
  CANCELLED_BY_CUSTOMER = "cancelled_by_customer",
  CANCELLED_BY_OPERATOR = "cancelled_by_operator",
  COMPLETED = "completed",
  NO_SHOW = "no_show",
  PAYMENT_FAILED = "payment_failed",
}

export enum CancellationPolicyType {
  FLEXIBLE = "flexible",
  MODERATE = "moderate",
  STRICT = "strict",
  CUSTOM = "custom",
  NON_REFUNDABLE = "non_refundable",
}

export enum MediaType {
  IMAGE = "image",
  VIDEO = "video",
}

export enum MediaShape {
  LANDSCAPE = "LANDSCAPE",
  PORTRAIT = "PORTRAIT",
  SQUARE = "SQUARE",
}

export enum LocationCategory {
  GENERAL = "GENERAL",
  HOTEL = "HOTEL",
  PORT = "PORT",
  AIRPORT = "AIRPORT",
  GEOGRAPHIC = "GEOGRAPHIC",
  OTHER = "OTHER",
}

export enum ServiceType {
  PICKUP = "PICKUP",
  DROPOFF = "DROPOFF",
}

export enum AdmissionInclusion {
  INCLUDED = "included",
  NOT_INCLUDED = "not_included",
  FREE = "free",
}

export enum FeatureType {
  INCLUSION = "INCLUSION",
  EXCLUSION = "EXCLUSION",
}

export enum FeatureCategory {
  EQUIPMENT = "EQUIPMENT",
  TRANSPORT_AMENITIES = "TRANSPORT_AMENITIES",
  FOOD_AND_DRINK = "FOOD_AND_DRINK",
  OTHER = "OTHER",
}

export enum ChargeUnit {
  PER_PERSON = "PER_PERSON",
  PER_BOOKING = "PER_BOOKING",
}

export enum ResellerType {
  DIRECT_PROVIDER = "DIRECT_PROVIDER",
  RESELLER = "RESELLER",
  NOT_RESELLER = "NOT_RESELLER",
}

export enum PricingType {
  TRAVELLER_BY_AGE_BAND = "TRAVELLER_BY_AGE_BAND",
  GROUP_PRICING = "GROUP_PRICING",
}

export enum AgeBand {
  ADULT = "ADULT",
  CHILD = "CHILD",
  YOUTH = "YOUTH",
  SENIOR = "SENIOR",
  INFANT = "INFANT",
}

export enum CancellationReason {
  // Weather Related
  WEATHER_SEVERE = "WEATHER_SEVERE",
  WEATHER_POOR_CONDITIONS = "WEATHER_POOR_CONDITIONS",
  // Operational
  STAFF_UNAVAILABLE = "STAFF_UNAVAILABLE",
  EQUIPMENT_FAILURE = "EQUIPMENT_FAILURE",
  SCHEDULING_ERROR = "SCHEDULING_ERROR",
  LOCATION_UNAVAILABLE = "LOCATION_UNAVAILABLE",
  // Participation
  MIN_PARTICIPANTS_NOT_REACHED = "MIN_PARTICIPANTS_NOT_REACHED",
  OVERBOOKING = "OVERBOOKING",
  // Customer-Driven
  CUSTOMER_REQUEST = "CUSTOMER_REQUEST",
  CUSTOMER_NO_SHOW = "CUSTOMER_NO_SHOW",
  CUSTOMER_CONDUCT_ISSUE = "CUSTOMER_CONDUCT_ISSUE",
  CUSTOMER_REQUIREMENTS_NOT_MET = "CUSTOMER_REQUIREMENTS_NOT_MET",
  // External
  REGULATORY_RESTRICTION = "REGULATORY_RESTRICTION",
  ACCESS_BLOCKED = "ACCESS_BLOCKED",
  FORCE_MAJEURE = "FORCE_MAJEURE",
  // Fallback
  OTHER = "OTHER",
}

export enum PriceUnit {
  PERSON = "PERSON",
  GROUP = "GROUP",
}

export enum OperatorStatusEnum {
  PENDING_APPROVAL = "pending_approval",
  ACTIVE = "active",
  SUSPENDED = "suspended",
  REJECTED = "rejected",
  ARCHIVED = "archived",
}

export interface Operator {
  id: string;
  merchantId: number;
  name: string;
  email: string;
  phone?: string;
  description?: string;
  website?: string;
  address?: string;
  status: OperatorStatusEnum;
  approvedAt: Date;
  approvedByAdminId: number;
}

export enum AffiliateStatusEnum {
  PENDING_APPROVAL = "pending_approval",
  ACTIVE = "active",
  SUSPENDED = "suspended",
  REJECTED = "rejected",
  ARCHIVED = "archived",
}

export interface Affiliate {
  id: string;
  merchantId: number;
  name: string;
  email?: string;
  status: AffiliateStatusEnum;
  approvedAt?: Date;
  approvedByAdminId?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

export enum AffiliateSiteStatus {
  DRAFT = "draft",
  PENDING = "pending",
  APPROVED = "approved",
  REJECTED = "rejected",
  CHANGES_REQUESTED = "changes_requested",
}

export interface AffiliateSiteConfig {
  id: string;
  affiliateId: string;
  subdomain: string;
  displayName: string;
  logoUrl?: string;
  faviconUrl?: string;
  primaryColor: string;
  secondaryColor?: string;
  welcomeMessage?: string;
  status: AffiliateSiteStatus;
  adminNotes?: string;
  version: number;
  createdAt: Date;
  updatedAt: Date;
}

export enum BookingCutoffType {
  START_TIME = "START_TIME",
  FIXED = "FIXED",
}

export interface MeetingPoint {
  searchString: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  mapUrl?: string;
  providerReference?: string;
  provider?: string;
  instructions?: string;
  externalReference?: string;
  categories?: string[];
}

export interface LocationSuggestionDetail {
  address: string;
  categories: string[];
  centre: {
    lat: number;
    long: number;
  };
  description: string;
  name: string;
  providerReference: string;
  searchString: string;
  locationAddress: {
    city: string;
    countryCode: string;
    country: string;
  };
}

export interface CancellationPolicy {
  id?: string;
  // operatorId?: string;
  // name: string;
  // description: string;
  type: CancellationPolicyType;
  refundWindowHours?: number;
  refundPercentage?: number;
  isNonRefundable: boolean;
  refundTiers?: {
    hoursBefore: number;
    percentage: number;
  }[];
  createdAt: Date;
  updatedAt: Date;
}

export interface ProductCategory {
  id: string;
  name: string;
  slug?: string;
  description?: string;
  parentCategoryId?: string;
  parentCategory?: ProductCategory;
}

export interface Tour {
  product: Product;
  highlights?: string;
  itinerary?: string;
  difficultyLevel?: string;
  durationMinutes: number;
  durationLabel?: string;
}

export interface Activity {
  product: Product;
  difficultyLevel?: string;
  durationMinutes: number;
}

export interface ProductMedia {
  id: string;
  productId: string;
  url: string;
  type: MediaType;
  shape?: MediaShape;
  sizes?: any; // JSONB field for multiple sizes
  isCover?: boolean;
  sortOrder?: number;
  altText?: string;
  caption?: string;
  externalReference?: string;
}

export interface PricingTierAgeBand {
  type: "AgeBand";
  netPriceCents: number;
  retailPriceCents: number;
  // mix max participants only for tier mode only
  minParticipants?: number;
  maxParticipants?: number;
}

export interface PricingTierGroup {
  type: "Group";
  netPriceCents: number;
  retailPriceCents: number;
  maxParticipants: number;
}

export type PricingTier = PricingTierAgeBand | PricingTierGroup;

export interface AgeBandDefinition {
  minAge: number;
  maxAge: number;
}

export interface ProductAddon {
  id: string;
  productId: string;
  name: string;
  description?: string;
  price: number;
  isRequired: boolean;
  availableFrom?: Date;
  availableTo?: Date;
  maxQuantityPerBooking: number;
  perParticipant: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface SchedulePattern {
  id: string;
  productId: string;
  startDate: Date;
  endDate: Date;
  daysOfWeek: string;
  startTime: string;
  endTime?: string;
  sessionLabel?: string;
  maxParticipants?: number;
  cutOffHours: number;
  status: "active" | "inactive" | "archived";
  recurrenceLimit?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface ItineraryStop {
  id: string;
  productId: string;
  description?: string;
  durationMinutes?: number;
  sortOrder: number;
  mediaUrl?: string;
  locationId: string;
  createdAt: Date;
  updatedAt: Date;
  location: MeetingPoint;
  admissionType: AdmissionInclusion;
}

export interface ProductAccessibility {
  id: string;
  productId: string;
  accessibilityOption: string;
  description?: string;
}

export interface ProductHealthSafety {
  id: string;
  productId: string;
  measureName: string;
  description: string;
}

export interface ProductSpecialRequirement {
  id: string;
  productId: string;
  requirement: string;
  isMandatory: boolean;
}
export enum CompletionStageEnum {
  BASIC_DETAILS = "basic-details",
  MEDIA = "media",
  THEMES_CATEGORIES = "themes-categories",
  MEETING_PICKUP = "meeting-pickup",
  TOUR_ACTIVITY_DETAILS = "tour-activity-details",
  ADDONS = "addons",
  SPECIAL = "special",
  ADDITIONAL_INFO = "additional-info",
  PRICING_SETUP = "pricing-setup",
  SCHEDULING = "scheduling",
  BOOKING_SETTINGS = "booking-settings",
  CANCELLATION_POLICY = "cancellation-policy",
  DONE = "done",
}

export enum TaxonomyFacet {
  THEME = "THEME",
  DIFFICULTY = "DIFFICULTY",
  CATEGORY = "CATEGORY",
}

export interface TaxonomyCategory {
  id: string;
  name: string;
  facet: TaxonomyFacet;
  parentId: string | null;
  externalReference: string;
  displayOrder: number | null;
  children?: TaxonomyCategory[];
  parent?: TaxonomyCategory;
}

export interface ProductTaxonomyMap {
  productId: string;
  categoryId: string;
  category: TaxonomyCategory;
}

export interface Product {
  id: string;
  operatorId: string;
  productType: ProductType;
  productClassification?: string;
  title: string;
  subtitle?: string;
  briefDescription?: string;
  description?: string;
  whatToBring?: string;
  goodToKnow?: string;
  status: ProductStatus;
  cancellationPolicyId?: string;
  language?: string;
  isPrivate?: boolean;
  isCustomizable?: boolean;
  isSkipTheLine?: boolean;
  contactPhone?: string;
  resellerType?: ResellerType;
  primaryLocationId?: string;
  externalId?: string;
  createdAt: Date;
  updatedAt: Date;
  lastFinishStage: CompletionStageEnum;
  cancellationPolicy?: CancellationPolicy;
  categories?: ProductCategory[];
  media?: ProductMedia[];
  features?: ProductFeature[];
  itineraryStops?: ItineraryStop[];
  bookingSettings?: ProductBookingSettings;
  tourDetails?: TourDetails;
  activityDetails?: Activity;
  meetingPickupSetting?: MeetingPickUpSetting;
  difficultyLevel?: DifficultyLevel;
  accessibilities?: AccessibilityInfo[];
  healthRestrictions?: HealthRestrictionInfo[];
  generalInfos?: GeneralInfo[];
  productOptions?: ProductOption[];
  taxonomyMaps?: ProductTaxonomyMap[];
}

export interface ProductOption {
  id?: string;
  title: string;
  isDefault?: boolean;
  isPickupIncluded: boolean;
  optionDetals: ProductOptionCustomization[];
  startLocations: MeetingPoint[];
  seasons: ProductSeason[];
}

export interface ProductOptionCustomization {
  type: CustomizationTypeEnum;
  name: string;
  description: string;
  integerValue: number;
}

export enum PricingTimeModel {
  FIXED_START_TIMES = "FIXED_START_TIMES",
  TIME_RANGES = "TIME_RANGES",
}

export enum DayOfWeekEnum {
  SUNDAY = "SUNDAY",
  MONDAY = "MONDAY",
  TUESDAY = "TUESDAY",
  WEDNESDAY = "WEDNESDAY",
  THURSDAY = "THURSDAY",
  FRIDAY = "FRIDAY",
  SATURDAY = "SATURDAY",
}

export interface ProductSeason {
  id?: string;
  name: string;
  startDate: Date;
  endDate?: Date;
  timeModel: PricingTimeModel;
  isDefault?: boolean;
  pricingRecords: PricingRecord[];
}

export type AgeBandPricingTier = Record<AgeBand, PricingTierAgeBand[]>;
export type PricingPackage = {
  isAgeBandPricingMultiTier: boolean;
  ageBandPricingTiers?: AgeBandPricingTier;
  groupPricingTier?: PricingTierGroup;
  type: PricingType;
};
export interface PricingRecord {
  id?: string;
  daysOfWeek?: DayOfWeekEnum[];
  startTimes?: string[]; // for FIXED_START_TIMES
  dayTimeRanges?: Record<
    string,
    {
      start: string;
      end: string;
      hasBooking: boolean; // granular blocking on booking time, backlog
    }[]
  >; // for TIME_RANGES
  hasBooking: boolean;
  fixStartTimeBookingFlags: Record<string, Record<string, boolean>>; // granular blocking on booking time, backlog
  externalReference?: string;
  pricingPackage: PricingPackage;
}

export type AgeDefinition = Record<AgeBand, AgeBandDefinition>;

export enum CustomizationTypeEnum {
  ACCOMMODATIONS = "ACCOMMODATIONS",
  ADDITIONAL_ACTIVITY = "ADDITIONAL_ACTIVITY",
  DURATION = "DURATION",
  FOOD_AND_DRINK = "FOOD_AND_DRINK",
  VEHICLE_TYPE = "VEHICLE_TYPE",
  OTHER = "OTHER",
}

export interface AdditionalInfoItem {
  infoType: AdditionalInfoTypeEnum;
  isApplicable: boolean;
  customText?: string;
  sortOrder?: number;
  externalReference?: string;
}

export interface DifficultyLevelInfo extends AdditionalInfoItem {
  infoType:
    | AdditionalInfoTypeEnum.PHYSICAL_EASY
    | AdditionalInfoTypeEnum.PHYSICAL_MEDIUM
    | AdditionalInfoTypeEnum.PHYSICAL_HARD;
}

export interface AccessibilityInfo extends AdditionalInfoItem {
  infoType:
    | AdditionalInfoTypeEnum.WHEELCHAIR_ACCESSIBLE
    | AdditionalInfoTypeEnum.STROLLER_ACCESSIBLE
    | AdditionalInfoTypeEnum.PETS_WELCOME
    | AdditionalInfoTypeEnum.PUBLIC_TRANSPORTATION_NEARBY
    | AdditionalInfoTypeEnum.INFANTS_MUST_SIT_ON_LAPS
    | AdditionalInfoTypeEnum.INFANT_SEATS_AVAILABLE
    | AdditionalInfoTypeEnum.ACCESSIBILITY_FREE_TEXT;
}

export interface HealthRestrictionInfo extends AdditionalInfoItem {
  infoType:
    | AdditionalInfoTypeEnum.NO_BACK_PROBLEMS
    | AdditionalInfoTypeEnum.NO_PREGNANT
    | AdditionalInfoTypeEnum.NO_HEART_PROBLEMS
    | AdditionalInfoTypeEnum.HEALTH_FREE_TEXT;
}

export interface GeneralInfo extends AdditionalInfoItem {
  infoType:
    | AdditionalInfoTypeEnum.FREE_TEXT
    | AdditionalInfoTypeEnum.WHY_PEOPLE_LOVE_THIS
    | AdditionalInfoTypeEnum.WHAT_TO_BRING;
}

export enum AdditionalInfoTypeEnum {
  WHEELCHAIR_ACCESSIBLE = "WHEELCHAIR_ACCESSIBLE",
  STROLLER_ACCESSIBLE = "STROLLER_ACCESSIBLE",
  PETS_WELCOME = "PETS_WELCOME",
  PUBLIC_TRANSPORTATION_NEARBY = "PUBLIC_TRANSPORTATION_NEARBY",
  INFANTS_MUST_SIT_ON_LAPS = "INFANTS_MUST_SIT_ON_LAPS",
  INFANT_SEATS_AVAILABLE = "INFANT_SEATS_AVAILABLE",
  NO_BACK_PROBLEMS = "NO_BACK_PROBLEMS",
  NO_PREGNANT = "NO_PREGNANT",
  NO_HEART_PROBLEMS = "NO_HEART_PROBLEMS",
  PHYSICAL_EASY = "PHYSICAL_EASY",
  PHYSICAL_MEDIUM = "PHYSICAL_MEDIUM",
  PHYSICAL_HARD = "PHYSICAL_HARD",
  FREE_TEXT = "FREE_TEXT",
  HEALTH_FREE_TEXT = "HEALTH_FREE_TEXT",
  WHAT_TO_BRING = "WHAT_TO_BRING",
  WHY_PEOPLE_LOVE_THIS = "WHY_PEOPLE_LOVE_THIS",
  ACCESSIBILITY_FREE_TEXT = "ACCESSIBILITY_FREE_TEXT",
}

export enum PickupOptionTypeEnum {
  PICKUP_ONLY = "PICKUP_ONLY",
  MEET_AT_START_POINT = "MEET_AT_START_POINT",
  PICKUP_AND_MEET_AT_START_POINT = "PICKUP_AND_MEET_AT_START_POINT",
}

export interface MeetingPickUpSetting {
  meetingPoint: {
    endsAtStartPoint: boolean;
    startPoints: MeetingPoint[];
    endPoints: MeetingPoint[];
  };
  pickupOption: {
    type: PickupOptionTypeEnum;
    minutesBeforeDepartureTimeForPickup: number;
    customPickupAllowed: boolean;
    additionalInfo?: string;
  };
  pickupPoints?: {
    points: PickupPoint[];
  };
}

export interface PickupPoint {
  searchString: string;
  name: string;
  address: string;
  centre: LatLong;
  category: PickupPointCategory;
  providerReference?: string;
  provider?: string;
  externalReference?: string;
}

export enum PickupPointCategory {
  HOTEL = "HOTEL",
  AIRPORT = "AIRPORT",
  PORT = "PORT",
}

export enum GroupDefinitionEnum {
  BIKE = "BIKE",
  CAR = "CAR",
  AIRCRAFT = "AIRCRAFT",
  BOAT = "BOAT",
  BUS = "BUS",
  PACKAGE = "PACKAGE",
  GROUP = "GROUP",
  ROOM_OR_ACCOMMODATION = "ROOM_OR_ACCOMMODATION",
}

export interface LatLong {
  lat: number;
  long: number;
}

export interface BookingCutoffOverride {
  // example: '10:00:00',
  startTime: string;

  cutoffHours: number;
}

export interface ProductBookingSettings {
  confirmationType: ConfirmationType;
  bookingCutoffType: BookingCutoffType;
  defaultBookingCutoffHours: number;
  bookingCutoffFixedTime: string | null; // value: ..., 12:30:00, 1:00:00, 1:30:00, ...
  bookingCutoffFixedTimeOnDay: number | null;

  isAdultRequired?: boolean;
  priceUnit: PriceUnit;
  priceUnitGroup?: GroupDefinitionEnum;
  maxParticipants?: number;
  bookingCutoffOverrides: BookingCutoffOverride[];
  ageBandDefinitions?: AgeDefinition;
}

export interface FeatureAddon {
  category: FeatureCategory;
  title?: string;
  displayText?: string;
  description?: string;
  externalReference?: string;
}
export interface ProductFeature extends FeatureAddon {
  featureType: FeatureType;
}

export interface TourDetails {
  productId: string;
  highlights?: string;
  difficultyLevel?: string;
  durationMinutes: number;
  flexibleDurationFromMinutes?: number;
  flexibleDurationToMinutes?: number;
  durationLabel?: string;
  maxParticipants: number;
}

export interface ProductSession {
  id: string;
  productId?: string;
  productOptionId?: string;
  pricingRecordId?: string;
  sessionDate: Date;
  startTime: string;
  endTime?: string;
  sessionCapacity: number;
  bookedSpots: number;
  availableSpots?: number;
  status?: SessionStatus;
  statusReason?: string;
  externalId?: string;
  product?: Product;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ProductBlackoutDate {
  id: string;
  productId: string;
  operatorId?: string;
  date?: Date;
  startDate?: Date;
  endDate?: Date;
  reason?: string;
  affectsAllProductsForOperator: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProductAvailabilityOverride {
  id: string;
  productId: string;
  productSession?: ProductSession;
  date: Date;
  startTime?: string;
  endTime?: string;
  availableSpotsOverride?: number;
  priceOverride?: number;
  reason: string;
  isClosed: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Customer {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phone?: string;
  address?: string;
}

export interface BookingParticipant {
  id: string;
  bookingId: string;
  pricingTier: PricingTier;
  quantity: number;
  pricePerUnitAtBooking: number;
}

export interface BookingAddon {
  id: string;
  bookingId: string;
  productAddon: ProductAddon;
  quantity: number;
  pricePerUnitAtBooking: number;
  totalAddonPrice: number;
}

export interface PaymentSession {
  id: string;
  bookingId?: string;
  provider: string;
  providerSessionId?: string;
  status:
    | "pending"
    | "succeeded"
    | "failed"
    | "requires_action"
    | "refunded"
    | "partially_refunded"
    | "captured"
    | "requires_capture"
    | "canceled";
  amount: number;
  currency: string;
  paymentMethodDetails?: any;
  errorMessage?: string;
  data?: any;
  isAuthorized?: boolean;
  capturedAt?: Date;
  collectionAttempts?: number;
  collectionDueAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface PayoutBatch {
  id: string;
  payoutPeriodYear: number;
  payoutPeriodMonth: number;
  status: string;
  currency: string;
  scheduledPayoutDate: string;
  processedAt?: string;
}

export interface BookingPayout {
  bookingAmount: number;
  commissionRatePercentage: number;
  commissionAmount: number;
  netPayoutAmount: number;
  sessionDate: string;
  payoutBatch: PayoutBatch;
}

export interface EstimatedPayout {
  estimatedNetPayoutAmount: number;
  commissionRatePercentage: number;
  estimatedCommissionAmount: number;
  nextPayoutDate: string;
}

export interface Booking {
  id: string;
  product: Product;
  productSession: ProductSession;
  customer: Customer;
  status: BookingStatus;
  totalAmount: number;
  specialRequests?: string;
  isWaitlist: boolean;
  cancellationReason?: string;
  cancellationDate?: Date;
  cancelledByUserId?: string;
  confirmedAt?: Date;
  bookingReference: string;
  paymentSession?: PaymentSession;

  // Commission details (kept for backward compatibility)
  commissionRatePercentage?: number;
  commissionAmount?: number;
  isPaidOut?: boolean;

  // Payout details for operators
  payout?: BookingPayout;
  estimatedPayout?: EstimatedPayout;

  // Nested references
  participants: BookingParticipant[];
  addons: BookingAddon[];

  externalId?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProductWaitlist {
  id: string;
  product: Product;
  productSession: ProductSession;
  customer: Customer;
  requestedParticipants: number;
  status: "pending" | "notified" | "booked" | "declined" | "expired";
  notifiedAt?: Date;
  expiresAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface Review {
  id: string;
  booking: Booking;
  product: Product;
  customer: Customer;
  rating: number;
  title?: string;
  comment?: string;
  isVerifiedPurchase: boolean;
  status: "pending_approval" | "published" | "rejected" | "hidden";
  operatorReply?: string;
  repliedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface OperatorPayout {
  id: string;
  operatorId: string;
  amount: number;
  currency: string;
  status: "pending" | "processing" | "paid" | "failed" | "cancelled";
  payoutDate?: Date;
  transactionReference?: string;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

// Affiliate Dashboard interfaces
export interface AffiliateStatsResponse {
  totalReferrals: number;
  confirmedBookings: number;
  pendingBookings: number;
  cancelledBookings: number;
  totalCommissionEarned: number;
  paidCommission: number;
  pendingCommission: number;
}

export interface AffiliateStats {
  totalBookings: number;
  pendingPayouts: number;
  completedPayouts: number;
  totalPayouts: number;
}

export interface AffiliateBookingCustomer {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface AffiliateBookingProduct {
  id: string;
  title: string;
  productType: string;
}

export interface AffiliateBookingProductSession {
  id: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  bookedSpots: number;
  product: AffiliateBookingProduct;
}

export interface ParticipantCount {
  id: string;
  bookingId: string;
  pricingPackageTierId: string;
  quantity: number;
  pricePerUnitAtBooking: number;
}

export interface AffiliateBookingBooking {
  id: string;
  bookingReference: string;
  totalAmount: number;
  status: string;
  createdAt: string;
  productSession: AffiliateBookingProductSession;
  customer: AffiliateBookingCustomer;
  participantCounts: ParticipantCount[];
}

export interface AffiliateBookingResponse {
  id: string;
  commissionAmount: string;
  status: "pending" | "completed" | "processing";
  createdAt: string;
  booking: AffiliateBookingBooking;
  affiliateNotes: string | null;
}

export interface AffiliateBookingDisplay {
  id: string;
  bookingReference: string;
  customerName: string;
  customerEmail: string;
  productTitle: string;
  sessionDate: string;
  sessionTime: string;
  numberOfPeople: number;
  bookingDate: string;
  commissionAmount: number;
  status: "pending" | "completed" | "processing";
  affiliateNotes: string | null;
}

export interface AffiliatePayout {
  id: string;
  affiliateId: string;
  payoutPeriodYear: number;
  payoutPeriodMonth: number;
  totalBookings: number;
  totalCommissionAmount: string;
  currency: string;
  status: string;
  scheduledPayoutDate: string;
  processedAt: string | null;
  externalTransactionRef: string | null;
  failureReason: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PayoutDetailsBooking {
  id: string;
  bookingReference: string;
  commissionAmount: string;
  status: string;
  customer: {
    firstName: string;
    lastName: string;
    email: string;
  };
  productSession: {
    sessionDate: string;
    product?: {
      title: string;
    };
  };
}

export interface PayoutDetails {
  batch: AffiliatePayout;
  bookings: any[];
}

export type DashboardTab = "bookings" | "payouts";

// Rezdy Integration interfaces
export interface RezdyConnection {
  environment: string;
  webhookUrl?: string;
  connectionType?: string;
  supplierAlias?: string;
}

export interface RezdyStatus {
  connected: boolean;
  connectionType?: string;
  supplierAlias?: string;
  environment?: string;
  lastSyncAt?: Date;
  lastSyncStatus?: string;
}

export interface RezdyProduct {
  productCode: string;
  name: string;
  shortDescription?: string;
  advertisedPrice?: number;
  images?: { itemUrl: string }[];
}

export interface ImportedProduct {
  id: string;
  title: string;
  externalId: string;
}

export interface RezdyProductsResponse {
  importable: RezdyProduct[];
  alreadyImported: string[];
}

export interface ImportProductsResponse {
  message: string;
  data: ImportedProduct[];
}

// Operator Profile Update
export interface UpdateOperatorProfileDto {
  name?: string;
  description?: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  logoUrl?: string;
}
